<script lang="ts" setup>
import { computed, ref } from 'vue';
import UpdateItem from './UpdateItem.ce.vue';
import type { FilterArrayKeys, ISearchState } from '@/components/Search/types'
import { useI18n } from '@/plugins/i18n'
import FilterDropdown from '@/components/Search/FilterDropdown.ce.vue';
import SelectedFiltersHeader from '../Common/SelectedFiltersHeader/index.ce.vue'
import SaiPagination from '@/components/common/SaiPagination/index.ce.vue';
import NoResult from '../Common/NoResult/index.ce.vue';
import iconMain from '@/assets/img/icon-search-result-sort.svg';
import iconOpen from '@/assets/img/icon-search-result-sort-close.svg';
import iconClose from '@/assets/img/icon-search-result-sort-close.svg';
import { useSearchStore } from '@/components/Search/store/index.ts'
import { useSearchActions } from '@/components/Search/SearchResults/Composables/useSearchActions'
import { DEFAULT_PAGE_NO, DEFAULT_RECORDS_PER_PAGE, UPDATES_SEARCH_TAB, DATE_FORMAT, UPDATES_FILTER_KEYS } from '@/components/Search/constants';
import InitialEmptyResultView from '@/components/Search/SearchResults/Common/InitialEmptyResultView/index.ce.vue';
import { parse, differenceInDays, isValid } from 'date-fns'
import ExportSearch from '@/components/Search/SearchResults/Common/ExportSearch/index.ce.vue';
//refs
const page = ref(DEFAULT_PAGE_NO);
const recordsPerPage = ref(DEFAULT_RECORDS_PER_PAGE);


//store
const searchStore = useSearchStore()

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance


const { 
  getResultsRecordTotal,
  isResultLoaded,
  getDocOffset,
  clearAllFilters,
  removeFilter,
  saveFilters,
  handleExportFieldSelection,
  handleExportFormatSelection,
  handleExportSearchResults,
  handleExportClose
} = useSearchActions(UPDATES_SEARCH_TAB, UPDATES_FILTER_KEYS)



const gotToPage = (pageNum: number) => {
  searchStore.searchUpdates(true, pageNum);
};

const getTotalDays = computed(() => {
  const startDateRaw = searchStore['updates.facetFilter.dateStart']
  const endDateRaw = searchStore['updates.facetFilter.dateEnd']

  if (!startDateRaw || !endDateRaw) return ''

  // Parse strings or use Date directly
  const startDate =
    typeof startDateRaw === 'string'
      ? parse(startDateRaw, DATE_FORMAT, new Date())
      : startDateRaw

  const endDate =
    typeof endDateRaw === 'string'
      ? parse(endDateRaw, DATE_FORMAT, new Date())
      : endDateRaw

  // Validate
  if (!isValid(startDate) || !isValid(endDate)) return ''

  // Compute difference
  return Math.abs(differenceInDays(endDate, startDate))
});

const arrUpdatesSearchTips= [
  'noResultLatestSearchTip1',
  'noResultLatestSearchTip2'
];

 const exportFieldOptions: {key: string, val: string}[] = [
      {key: 'update_received_at', val: t('date')},
      {key: 'update_status', val: t('updateStatus')},
      {key: 'doc_publisher_name', val: t('publisher')},
      {key: 'doc_name', val: t('documentName')},
      {key: 'doc_title', val: t('documentTitle')},
      {key: 'updated_version', val: t('updatedVersion')},
      {key: 'change_summary', val: t('changeSummary')}
  ];
</script>

<template>
    <InitialEmptyResultView  v-if="!isResultLoaded && !searchStore[`${UPDATES_SEARCH_TAB}.result.loading`]" 
    :title="t('findUpdatedStandards')" :subTitle ="t('typeInSearchToGetStarted')"></InitialEmptyResultView>
    <div v-if="isResultLoaded && getResultsRecordTotal() === 0" class="search-result search-result--noresult updates-no-result">
        <template v-if="searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.nohits">
          <NoResult :searchTerms="searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.searchTerms" :searchTips="arrUpdatesSearchTips"/>
        </template>
    </div>
    <div v-if="isResultLoaded && getResultsRecordTotal() > 0" class="search-result">
      <SelectedFiltersHeader :filterKeys="UPDATES_FILTER_KEYS" @removeFilter="removeFilter" @clearAll="clearAllFilters" @saveFilters="saveFilters"/>
      <div class="search-result--summary-for-mobile">
        {{t('updatesIn', [getResultsRecordTotal(), getTotalDays])}}
      </div>
      <div class="search-result--header">
        <div class="search-result--header-left">&nbsp;</div>
        <div class="search-result--header-center">{{t('updatesIn', [getResultsRecordTotal(), getTotalDays])}}</div>
        <div class="search-result--header-right">
            <FilterDropdown
              className="dropdown result-sort updates-sort" 
              :placeholder="t('sort')" 
              :placeholderPrefix="`${t('sort')}:`"
              name="updates_sort" 
              :multiple="false"
              field="updates.result.sort"
              lookupField="lookUpOptions.updates.sortOptions"
              :hasClear="false" 
              :iconMain="iconMain"
              :iconOpenMain="iconMain"
              :iconOpen="iconOpen"
              :iconClose="iconClose"
              :searchable="false"
              :showMoreLink="false"
              :closeOnClickOutside="true"
              :showSelectCountInHeader="false"
              :closeOnSelect="true"
              :translate="true"
              :showSelectedInPlaceholder="true"
              @updateSelectedItems="() => searchStore.searchUpdates(true)"
              />
            <ExportSearch 
              :name="UPDATES_SEARCH_TAB"
              :fieldOptions="exportFieldOptions"
              :status="searchStore[`${UPDATES_SEARCH_TAB}.export.status`]"
              :selectedFormat="searchStore[`${UPDATES_SEARCH_TAB}.export.format`]"
              :totalFound="searchStore[`${UPDATES_SEARCH_TAB}.export.totalFound`] ?? 0"
              :selectedFields="searchStore[`${UPDATES_SEARCH_TAB}.export.selectedFields`] ?? []"
              :searchContext="searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.searchContext" 
              :note="t('exportNoteUpdates')"
              :maxExportLimit="500"
              @handleExportFieldSelection="handleExportFieldSelection"
              @handleExportFormatSelection="handleExportFormatSelection"
              @handleExportClose="handleExportClose"
              @handleExportSearchResults="handleExportSearchResults">
            </ExportSearch>
        </div>
      </div>
      <div class="search-result-items updates-result-items">
          <UpdateItem v-for="(record, index) in searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.records" 
            :docOffset="getDocOffset(index)"
            :item="record" 
            :searchContext="searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.searchContext" 
            :key="index"/>
      </div>
      <div class="search-result--footer">
        <SaiPagination 
            :current-page="searchStore[`${UPDATES_SEARCH_TAB}.result.page`]"
            :total-items="searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.hits ?? 0"
            :page-size="searchStore[`${UPDATES_SEARCH_TAB}.resultData`]?.recordsPerPage ?? 0"
            @goToPage="gotToPage"></SaiPagination>
      </div>
    </div>
</template>
<style lang="scss">
    @use './style.scss';
</style>